import React, { useEffect, useState } from 'react';
import { Globe2, Check, RotateCcw, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { getMarketOverride, clearMarketOverride, captureMarketOverrideFromURL } from '../../utils/marketOverride';
import { toast } from 'sonner';

/**
 * AdminMarketTester
 *
 * Lets the platform owner switch the auto-detected country for the
 * current tab between UK / IN / CA without using a VPN. This forwards
 * `?cc=IN` to /api/compliance/me and /api/geo/country, so every UI
 * element that depends on country detection (compliance copy, currency,
 * pricing) renders as if the visitor were in that market.
 *
 * Scope: session-only. Closing the tab clears the override. Other users
 * are unaffected — the override lives in this tab's sessionStorage.
 */
const MARKETS = [
  { code: 'UK', label: '🇬🇧 United Kingdom', currency: '£ GBP', regime: 'UK CPRs + UK-GDPR' },
  { code: 'IN', label: '🇮🇳 India',          currency: '₹ INR', regime: 'DPDPA + RERA' },
  { code: 'CA', label: '🇨🇦 Canada',         currency: 'C$ CAD', regime: 'PIPEDA' },
];

const AdminMarketTester = () => {
  const [active, setActive] = useState(getMarketOverride());

  // Pick up `?test=IN` if the admin lands here via a shareable test URL
  useEffect(() => {
    const captured = captureMarketOverrideFromURL();
    if (captured) setActive(captured);
  }, []);

  const apply = (cc) => {
    // Persist + reload so every component re-runs detection with the new value.
    try { sessionStorage.setItem('shopperden_test_country', cc); } catch (_e) { /* ignore */ }
    setActive(cc);
    toast.success(`Now testing as ${cc}. Reloading…`);
    setTimeout(() => window.location.reload(), 400);
  };

  const reset = () => {
    clearMarketOverride();
    setActive(null);
    toast.success('Override cleared. Reloading…');
    setTimeout(() => window.location.reload(), 400);
  };

  const openInTab = (cc, path = '/register') => {
    const url = `${window.location.origin}${path}?test=${cc}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="admin-market-tester">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 bg-[#E07A3A]/10 border border-[#E07A3A]/30 flex items-center justify-center flex-shrink-0 rounded">
          <Globe2 className="h-5 w-5 text-[#E07A3A]" />
        </div>
        <div>
          <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A]">Test Region Detection</h3>
          <p className="text-sm text-slate-500 mt-1">
            Switch the auto-detected market for <strong>this tab only</strong>. Useful when you want
            to validate Indian (₹) or Canadian (C$) pricing &amp; compliance without using a VPN.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        {MARKETS.map((m) => {
          const isActive = active === m.code;
          return (
            <button
              key={m.code}
              type="button"
              onClick={() => apply(m.code)}
              className={`text-left p-4 rounded border-2 transition-all ${
                isActive
                  ? 'border-[#E07A3A] bg-[#FEF8EC] shadow-sm'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              data-testid={`market-test-${m.code.toLowerCase()}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-sm text-[#0F172A]">{m.label}</span>
                {isActive && <Check className="h-4 w-4 text-[#E07A3A]" />}
              </div>
              <p className="text-[11px] text-slate-500">{m.currency}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{m.regime}</p>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-[10px] tracking-wider uppercase font-bold text-[#E07A3A]">
                  {isActive ? 'Testing now' : 'Switch to'}
                </span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openInTab(m.code, '/register'); }}
                  className="ml-auto inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-[#0F172A]"
                  title="Open the registration page in a fresh tab forced to this market"
                  data-testid={`market-test-${m.code.toLowerCase()}-open-register`}
                >
                  Open /register <ExternalLink className="h-3 w-3" />
                </button>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100">
        <div className="text-xs text-slate-500">
          {active ? (
            <>Active override: <strong className="text-[#E07A3A]">{active}</strong> · only this tab is affected.</>
          ) : (
            <>No override active. Detection runs as normal (CF header → timezone → Accept-Language).</>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={reset}
            disabled={!active}
            data-testid="market-test-reset"
            className="text-xs"
          >
            <RotateCcw className="h-3 w-3 mr-1.5" />
            Clear &amp; reload
          </Button>
        </div>
      </div>

      <details className="mt-4 text-xs text-slate-500" data-testid="market-test-howto">
        <summary className="cursor-pointer text-[#E07A3A] font-semibold tracking-wider uppercase text-[10px]">
          Share a test link with a colleague
        </summary>
        <p className="mt-2 leading-relaxed">
          Append <code className="bg-slate-100 px-1.5 py-0.5 rounded">?test=IN</code> (or
          <code className="bg-slate-100 px-1.5 py-0.5 rounded ml-1">?test=CA</code>,
          <code className="bg-slate-100 px-1.5 py-0.5 rounded ml-1">?test=UK</code>) to any
          Shopperden URL. The override is captured into <code className="bg-slate-100 px-1.5 py-0.5 rounded">sessionStorage</code>
          on first load and persists until the tab is closed.
          Use <code className="bg-slate-100 px-1.5 py-0.5 rounded">?test=clear</code> to remove it.
        </p>
        <p className="mt-2 leading-relaxed">
          Example: <code className="bg-slate-100 px-1.5 py-0.5 rounded">{`${typeof window !== 'undefined' ? window.location.origin : 'https://shopperden.com'}/register?test=IN`}</code>
        </p>
      </details>
    </div>
  );
};

export default AdminMarketTester;
